import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LIST_VIEW_LIMIT, SUCCESS_STATUS, SUPPORT_LIST } from "../../constants";
import { LOADING_KEYS } from "../../constants/loadingKeys";
import { useListQuery } from "../../hooks/useListQuery";
import type { ApiEnvelope } from "../../types/api";
import * as api from "./api";
import type { ApplicationPageOption, SupportListData, SupportQuestionsData, SupportTopic } from "./types";

export const supportQueryKeys = {
  questions: (supportDataId: string | number | undefined) => ["support", "questions", supportDataId] as const,
  applicationPageOptions: () => ["support", "applicationPageOptions"] as const,
  topics: (payload: { page?: string; application?: string }) => ["support", "topics", payload] as const,
};

/** List/search/sort/paginate for the Contextual Support admin list screen. */
export function useSupportList(params: { offset: number; search?: string; sortType?: string }) {
  const query = useListQuery<ApiEnvelope<SupportListData>>({
    url: SUPPORT_LIST,
    method: "GET",
    limit: LIST_VIEW_LIMIT,
    offset: params.offset,
    search: params.search,
    sortType: params.sortType,
    loadingKey: LOADING_KEYS.GET_SUPPORT,
  });

  const supportList: SupportListData = query.data && query.data.status === SUCCESS_STATUS ? query.data.data ?? {} : {};

  return { ...query, supportList };
}

export function useSupportQuestions(supportDataId: string | number | undefined) {
  return useQuery({
    queryKey: supportQueryKeys.questions(supportDataId),
    queryFn: () => api.fetchSupportQuestions(supportDataId as string | number),
    enabled: !!supportDataId,
    select: (envelope): SupportQuestionsData => (envelope.status === SUCCESS_STATUS ? envelope.data ?? {} : {}),
  });
}

export function useApplicationPageOptions() {
  return useQuery({
    queryKey: supportQueryKeys.applicationPageOptions(),
    queryFn: () => api.fetchApplicationPageOptions(),
    select: (envelope): ApplicationPageOption[] => (envelope.status === SUCCESS_STATUS ? envelope.data?.options ?? [] : []),
  });
}

export function useSupportTopics(payload: { page?: string; application?: string }) {
  return useQuery({
    queryKey: supportQueryKeys.topics(payload),
    queryFn: () => api.fetchSupportTopics(payload),
    enabled: !!payload.application,
    select: (envelope): SupportTopic[] => (envelope.status === SUCCESS_STATUS ? envelope.data ?? [] : []),
  });
}

export function useSaveSupportQuestionsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.saveSupportQuestions,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: supportQueryKeys.questions(variables.id),
      });
    },
  });
}

export function useCloneSupportQuestionsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.cloneSupportQuestions,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: supportQueryKeys.questions(variables.supportDataId),
      });
    },
  });
}
